import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { SearchService } from '@psu-pennstateit-swe/apis/search';
import { REQUIRE_AUTH_HEADER } from '@psu-pennstateit-swe/utils/security';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { APP_PROPERTIES } from '../../environments/fortress.properties.model';
import { ClientFortressType, FortressPermissionObject, Role } from './fortress.models';

export interface FortressSearchResult {
  value: string;
  description?: string;
  type: ClientFortressType;
}

@Injectable({
  providedIn: 'root',
})
export class FortressSearchService {
  searchErrors = computed(() =>
    [this.userSearchError(), this.roleSearchError(), this.permObjSearchError()].filter((e) => e !== null),
  );
  private userSearchError = signal<string | null>(null);
  private roleSearchError = signal<string | null>(null);
  private permObjSearchError = signal<string | null>(null);
  private searchService = inject(SearchService);
  private httpClient = inject(HttpClient);
  private fortressUrl = inject(APP_PROPERTIES).fortress.url;
  private httpHeaders = new HttpHeaders({
    [REQUIRE_AUTH_HEADER]: 'true',
  });

  searchResource = (searchTerm: Signal<string | undefined>) =>
    rxResource({
      params: () => searchTerm(),
      stream: ({ params: parsedTerm }): Observable<FortressSearchResult[]> => {
        this.userSearchError.set(null);
        this.roleSearchError.set(null);
        this.permObjSearchError.set(null);
        return forkJoin([
          // Search users
          parsedTerm.length < 3
            ? of([])
            : this.searchService.getPeople(parsedTerm, { serviceAccount: false }).pipe(
                map((results) =>
                  results.map(
                    (r): FortressSearchResult => ({ value: r.userid ?? '', description: r.displayName, type: 'USER' }),
                  ),
                ),
                catchError(() => {
                  this.userSearchError.set('User Search: A server error occurred attempting to retrieve users');
                  return of([] as FortressSearchResult[]);
                }),
              ),
          // Search Roles
          this.httpClient
            .get<Role[]>(`${this.fortressUrl}roles`, { headers: this.httpHeaders, params: { name: parsedTerm } })
            .pipe(
              map((results) =>
                results.map((r): FortressSearchResult => ({ value: r.name, description: r.description, type: 'ROLE' })),
              ),
              catchError(() => {
                this.roleSearchError.set('Role Search: A server error occurred attempting to retrieve roles');
                return of([] as FortressSearchResult[]);
              }),
            ),
          // Search Permissions Objects
          this.httpClient
            .get<FortressPermissionObject[]>(`${this.fortressUrl}permissions/objects`, {
              headers: this.httpHeaders,
              params: { name: parsedTerm },
            })
            .pipe(
              map((results) =>
                results.map((r): FortressSearchResult => ({ value: r.objName, type: 'PERMISSION_OBJECT' })),
              ),
              catchError(() => {
                this.permObjSearchError.set(
                  'Permission Object Search: A server error occurred attempting to retrieve permission objects',
                );
                return of([] as FortressSearchResult[]);
              }),
            ),
        ]).pipe(
          map((results) =>
            [...results[0], ...results[1], ...results[2]]
              .map((result) => ({
                ...result,
                // evaluate "partial matching" by character frequency or overlap to assess a sort score where a higher score means a better match
                score: parsedTerm
                  .toLowerCase()
                  .split('')
                  .filter((char) => result.value.toLowerCase().includes(char)).length,
              }))
              // Sort by score with any matching scores being sorted alphabetically
              .sort((a, b) => (a.score !== b.score ? b.score - a.score : a.value.localeCompare(b.value))),
          ),
        );
      },
    });
}
